// SUPERADMIN-only destructive action. Two guards before firing:
//   1. The operator types the club's exact name into the input.
//   2. The "Delete forever" button arms on first click; a second click within
//      3 seconds actually fires the DELETE. Pressing cancel or letting the
//      timer expire resets to step 1.

import { useEffect, useRef, useState } from "react";
import { AlertCircle, AlertTriangle, Loader2 } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Alert,
  AlertDescription,
  Button,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@sportscanner/ui";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
} from "@/components/responsive-dialog";
import { apiDelete, getApiErrorCode } from "@/lib/api";

const ARM_TIMEOUT_MS = 3000;

interface Props {
  club: { id: string; name: string } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted: () => Promise<void> | void;
}

export default function DeleteClubDialog({
  club,
  open,
  onOpenChange,
  onDeleted,
}: Props) {
  const { t } = useTranslation();
  const [confirmText, setConfirmText] = useState("");
  const [armed, setArmed] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const armTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function disarm() {
    setArmed(false);
    if (armTimerRef.current) {
      clearTimeout(armTimerRef.current);
      armTimerRef.current = null;
    }
  }

  useEffect(() => {
    if (open) {
      setConfirmText("");
      setErrorCode(null);
      disarm();
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (armTimerRef.current) clearTimeout(armTimerRef.current);
    };
  }, []);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!club) throw new Error("no club");
      await apiDelete(`/api/admin/clubs/${club.id}`, {
        confirm: confirmText.trim(),
      });
    },
    onSuccess: async () => {
      setErrorCode(null);
      disarm();
      toast.success(t("clubs.delete.toast.success", { name: club?.name }));
      onOpenChange(false);
      await onDeleted();
    },
    onError: (err) => {
      disarm();
      setErrorCode(getApiErrorCode(err));
    },
  });

  const nameMatches = !!club && confirmText.trim() === club.name;

  function handleDeleteClick() {
    if (!nameMatches || mutation.isPending) return;
    if (!armed) {
      setArmed(true);
      armTimerRef.current = setTimeout(() => {
        setArmed(false);
        armTimerRef.current = null;
      }, ARM_TIMEOUT_MS);
      return;
    }
    disarm();
    mutation.mutate();
  }

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(o) => {
        if (mutation.isPending) return;
        onOpenChange(o);
      }}
    >
      <ResponsiveDialogContent className="md:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" />
            {t("clubs.delete.title")}
          </DialogTitle>
          <DialogDescription>
            {t("clubs.delete.description", { name: club?.name ?? "" })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <p className="font-medium">{t("clubs.delete.warning.heading")}</p>
              <ul className="mt-1 list-disc pl-4 text-sm space-y-0.5">
                <li>{t("clubs.delete.warning.bookings")}</li>
                <li>{t("clubs.delete.warning.courts")}</li>
                <li>{t("clubs.delete.warning.members")}</li>
                <li>{t("clubs.delete.warning.staff")}</li>
              </ul>
            </AlertDescription>
          </Alert>

          {errorCode && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t(`errors.api.${errorCode}`)}
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="delete-club-confirm" className="text-sm">
              {t("clubs.delete.confirmLabel", { name: club?.name ?? "" })}
            </Label>
            <Input
              id="delete-club-confirm"
              type="text"
              value={confirmText}
              onChange={(e) => {
                setConfirmText(e.target.value);
                // Editing the name disarms — operator has to start the
                // double-click again from a clean state.
                if (armed) disarm();
              }}
              placeholder={club?.name ?? ""}
              disabled={mutation.isPending}
              autoComplete="off"
              className="rounded-lg border-neutral-200"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            onClick={handleDeleteClick}
            disabled={!nameMatches || mutation.isPending}
            className={
              armed
                ? "bg-red-700 hover:bg-red-800 text-white animate-pulse"
                : "bg-red-600 hover:bg-red-700 text-white"
            }
          >
            {mutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : armed ? (
              t("clubs.delete.armedLabel")
            ) : (
              t("clubs.delete.deleteLabel")
            )}
          </Button>
        </DialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
